// スケルトン弓兵：距離を保ち、定期的に矢を撃つ
import Phaser from 'phaser';
import { Enemy } from '../Enemy';
import { DIR_ROW } from '../../data/Directions';

const PREFERRED_DISTANCE = 130; // この距離を維持しようとする
const ARROW_SPEED = 260;

export interface ArcherCallbacks {
  spawnArrow: (x: number, y: number, vx: number, vy: number) => void;
}

export class Archer extends Enemy {
  private callbacks: ArcherCallbacks;

  constructor(scene: Phaser.Scene, x: number, y: number, callbacks: ArcherCallbacks) {
    super(scene, x, y, 'archer');
    this.callbacks = callbacks;
  }

  static defineAnimations(scene: Phaser.Scene): void {
    const dirs = ['down', 'left', 'right', 'up'] as const;
    for (const d of dirs) {
      const key = `archer-walk-${d}`;
      if (scene.anims.exists(key)) continue;
      const start = DIR_ROW[d] * 3;
      scene.anims.create({
        key,
        frames: scene.anims.generateFrameNumbers('enemy_archer', {
          start,
          end: start + 2,
        }),
        frameRate: 5,
        repeat: -1,
      });
    }
  }

  // 距離維持＋射撃 のAI（基底の tick を上書き）
  override tick(playerX: number, playerY: number, _delta: number): void {
    if (!this.active) return;
    const dist = Phaser.Math.Distance.Between(this.x, this.y, playerX, playerY);
    const spec = this.spec();

    // 視認範囲外は静止
    if (dist > spec.detectRange) {
      this.setVelocity(0, 0);
      return;
    }
    // プレイヤーが近すぎたら離れる
    const angleToPlayer = Phaser.Math.Angle.Between(this.x, this.y, playerX, playerY);
    if (dist < PREFERRED_DISTANCE - 20) {
      const away = angleToPlayer + Math.PI;
      this.setVelocity(Math.cos(away) * spec.speed, Math.sin(away) * spec.speed);
    } else if (dist > PREFERRED_DISTANCE + 20) {
      this.setVelocity(Math.cos(angleToPlayer) * spec.speed, Math.sin(angleToPlayer) * spec.speed);
    } else {
      this.setVelocity(0, 0);
    }
    // 向きはプレイヤー方向
    const vx = Math.cos(angleToPlayer);
    const vy = Math.sin(angleToPlayer);
    if (Math.abs(vx) > Math.abs(vy)) this.facing = vx > 0 ? 'right' : 'left';
    else this.facing = vy > 0 ? 'down' : 'up';
    this.playWalkAnim();

    // 攻撃判定：射程内＋クールダウン明け
    if (dist <= spec.attackRange && this.attackCooldownMs <= 0) {
      this.attackCooldownMs = spec.cooldownMs;
      const ang = Phaser.Math.Angle.Between(this.x, this.y, playerX, playerY);
      this.callbacks.spawnArrow(
        this.x,
        this.y,
        Math.cos(ang) * ARROW_SPEED,
        Math.sin(ang) * ARROW_SPEED,
      );
    }
  }

  protected override playWalkAnim(): void {
    const key = `archer-walk-${this.facing}`;
    if (this.anims.currentAnim?.key !== key) this.anims.play(key, true);
  }
}
